import { useState, useEffect, useRef } from 'react';
import { api } from '../../../api.js';

function formatCost(usd) {
  if (usd == null) return '—';
  if (usd < 0.01) return `$${usd.toFixed(4)}`;
  return `$${usd.toFixed(2)}`;
}

/**
 * DeepSearchPopover — two-phase upgrade flow:
 *   1. Fetch cost preview on mount.
 *   2. Show estimate; user confirms or cancels.
 *   3. Apply with approved=true.
 */
export default function DeepSearchPopover({ caseId, ids, count, anchorRef, onClose, onCompleted }) {
  const [estimate, setEstimate] = useState(null);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    function handleClick(e) {
      if (ref.current?.contains(e.target)) return;
      if (anchorRef?.current?.contains(e.target)) return;
      onClose?.();
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [anchorRef, onClose]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const est = await api.post(`/exploration/case/${caseId}/upgrade-tier`, {
          document_ids: ids,
          target_tier: 'deep',
          approved: false,
        });
        if (!cancelled) {
          setEstimate(est);
          setBusy(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Estimate failed');
          setBusy(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [caseId, ids]);

  async function handleConfirm() {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/exploration/case/${caseId}/upgrade-tier`, {
        document_ids: ids,
        target_tier: 'deep',
        approved: true,
      });
      onCompleted?.(`Upgrading ${count} file${count === 1 ? '' : 's'} to deep search`);
    } catch (err) {
      setError(err.message || 'Upgrade failed');
      setBusy(false);
    }
  }

  return (
    <div ref={ref} className="bulk-popover" role="dialog" aria-label="Upgrade to deep search">
      <div className="bulk-popover-title">
        🔬 Upgrade {count} file{count === 1 ? '' : 's'} to deep search?
      </div>
      <div className="bulk-popover-body">
        {busy && !estimate && !error && <span>Estimating cost…</span>}
        {estimate && (
          <>
            Estimated cost: <strong>{formatCost(estimate.total_cost_usd)}</strong>
          </>
        )}
      </div>
      {error && <div className="bulk-popover-error">{error}</div>}
      <div className="bulk-popover-actions">
        <button type="button" className="btn btn-sm" onClick={onClose} disabled={busy && !estimate}>Cancel</button>
        <button
          type="button"
          className="btn btn-sm btn-primary"
          onClick={handleConfirm}
          disabled={busy || !estimate}
        >
          {busy && estimate ? 'Upgrading…' : 'Yes, upgrade'}
        </button>
      </div>
    </div>
  );
}
